import {
  useState,
  useRef,
  useEffect,
  type KeyboardEvent,
  type ClipboardEvent,
  type FC,
} from "react";
import { Input, type InputHandle } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import "../../styles/common/GxEmailOtpVerificationModal.scss";

type OTPStatus = "idle" | "loading" | "success" | "error";

interface GXEmailOtpVerificationModalProps {
  email?: string | null;
  resendCooldown?: number;
  onVerified: () => void;
  onSubmit: (code: string) => Promise<boolean>;
  onCancel: () => void;
}

const GXEmailOtpVerificationModal: FC<GXEmailOtpVerificationModalProps> = ({
  email,
  resendCooldown = 179,
  onVerified,
  onSubmit,
  onCancel,
}) => {
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const [status, setStatus] = useState<OTPStatus>("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [timer, setTimer] = useState<number>(resendCooldown);
  const [inputError, setInputError] = useState<boolean>(false);
  const inputRefs = useRef<(InputHandle | null)[]>([]);

  useEffect(() => {
    if (timer <= 0) return;
    const id = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(id);
  }, [timer]);

  const formatTimer = (s: number): string => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec?.toString()?.padStart(2, "0")}`;
  };

  const handleChange = (index: number, value: string): void => {
    const digit = value.replaceAll(/\D/g, "").slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    setErrorMsg("");
    setInputError(false);
    if (digit && index < 5) inputRefs.current[index + 1]?.element?.focus();
  };

  const handleKeyDown = (
    index: number,
    e: KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.element?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0)
      inputRefs.current[index - 1]?.element?.focus();
    if (e.key === "ArrowRight" && index < 5)
      inputRefs.current[index + 1]?.element?.focus();
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replaceAll(/\D/g, "")
      .slice(0, 6);
    const next = [...otp];
    pasted.split("").forEach((ch, i) => {
      next[i] = ch;
    });
    setOtp(next);
    inputRefs.current[Math.min(pasted.length, 5)]?.element?.focus();
  };

  const handleVerify = async (): Promise<void> => {
    const code = otp.join("");
    if (code.length < 6) {
      setErrorMsg("Please enter all 6 digits.");
      setInputError(true);
      return;
    }
    setStatus("loading");

    let isValid = false;
    try {
      if (onSubmit) {
        isValid = await onSubmit(code);
      }
    } catch {
      setStatus("error");
      setErrorMsg("Verification failed. Please try again.");
      setInputError(true);
      setTimeout(() => {
        setInputError(false);
        setStatus("idle");
      }, 600);
      return;
    }

    if (isValid) {
      setStatus("success");
      onVerified();
    } else {
      setStatus("error");
      setErrorMsg("Invalid OTP. Please try again.");
      setInputError(true);
      setTimeout(() => {
        setInputError(false);
        setStatus("idle");
      }, 600);
    }
  };

  const handleResend = (): void => {
    setTimer(resendCooldown);
    setOtp(new Array(6).fill(""));
    setErrorMsg("");
    setInputError(false);
    setStatus("idle");
    setTimeout(() => inputRefs.current[0]?.element?.focus(), 50);
  };

  const isComplete: boolean = otp.every((d) => d !== "");

  return (
    <div className="gx-otp-scene">
      <div className="gx-otp-card">
        <h2 className="gx-otp-title gx-p27-r">
          Verify <strong className="gx-p27-b">One-Time Password</strong>
        </h2>
        <p className="gx-otp-subtitle gx-p14-r">
          Enter the 6-digit code from your Google Authenticator app.
        </p>

        {/* Email + Edit */}
        {email && <div className="gx-otp-email-row">{email}</div>}

        {/* OTP Kendo Inputs */}
        <div className={`gx-otp-inputs ${email ? "" : "gx-otp-inputs-margin-top"}`}>
          {otp.map((digit, i) => {
            return (
              <div
                key={i}
                className={`gx-otp-digit-wrap${digit ? " filled" : ""}${inputError ? " has-error" : ""}`}
              >
                <Input
                  ref={(el) => {
                    inputRefs.current[i] = el;
                  }}
                  value={digit}
                  maxLength={1}
                  inputMode="numeric"
                  className="gx-font-medium"
                  autoFocus={i === 0}
                  onChange={(e) => handleChange(i, String(e.value ?? ""))}
                  onKeyDown={(e) =>
                    handleKeyDown(
                      i,
                      e as unknown as KeyboardEvent<HTMLInputElement>,
                    )
                  }
                  onPaste={(e) =>
                    handlePaste(
                      e as unknown as ClipboardEvent<HTMLInputElement>,
                    )
                  }
                />
              </div>
            );
          })}
        </div>

        {errorMsg && <div className="gx-otp-error-msg gx-p14-md">{errorMsg}</div>}

        {/* Verify Button (Kendo) */}
        <div className="gx-otp-cancel-verify-btn">
          <div className={`gx-otp-cancel-wrap ${email ? "gx-btn-margin-bottom" : ""}`}>
            <Button onClick={onCancel}>
             <span className="gx-p15-b"> {"Cancel"}</span>
            </Button>
          </div>
          <div className={`gx-otp-verify-wrap ${email ? "gx-btn-margin-bottom" : ""}`}>
            <Button
              className={`${status === "loading" ? "is-loading" : ""}`}
              themeColor="dark"
              disabled={status === "loading" || !isComplete}
              onClick={handleVerify}
            >
              <span className="gx-p15-b">{status === "loading" ? "Verifying..." : "Verify OTP"}</span>
            </Button>
          </div>
        </div>

        {/* Resend Button (Kendo) */}
        {email && (
          <div className="gx-otp-resend-wrap">
            <Button
              fillMode="flat"
              disabled={timer > 0}
              onClick={handleResend}
              className="gx-p15-r"
            >
              Didn't receive an email? Resend
              {timer > 0 ? ` (${formatTimer(timer)})` : ""}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GXEmailOtpVerificationModal;
